import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, Linking, ScrollView, Modal } from 'react-native';
import { StyleSheet, Platform } from 'react-native';
import { colors } from '../styles/theme';
import { useNavigation } from '@react-navigation/native';
import { BREAKPOINTS, getResponsiveValue } from '../styles/breakpoints';

const styles = StyleSheet.create({
  footerContainer: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#E9E9E9',
    backdropFilter: 'none',
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
    zIndex: 100,
    paddingHorizontal: 20,
    paddingVertical: 30,
  },
  footerContainerMobile: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 15,
  },
  footer: {
    padding: 5,
    justifyContent: 'center',
    opacity: 0.6,
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease',
      ':hover': {
        opacity: 1
      }
    }),
  },
  middleSection: {
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.6,
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease',
      ':hover': {
        opacity: 1
      }
    }),
  },
  middleSectionText: {
    color: '#000000',
    fontFamily: 'Playpen Sans',
    fontWeight: '500',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 10,
      desktop: 10
    }),
    textAlign: 'center',
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease',
    }),
  },
  footerText: {
    color: '#000000',
    fontFamily: 'Playpen Sans',
    fontWeight: '500',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 10,
      desktop: 10
    }),
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease',
    }),
  },
  linkContainer: {
    display: 'inline',
    fontFamily: 'Playpen Sans',
    fontWeight: '500',
  },
  link: {
    color: '#2ecc71',
    opacity: 1,
    fontFamily: 'VastagoGrotesk',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 10,
      desktop: 10
    }),
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'opacity 0.3s ease',
    }),
  },
  disclaimerContainer: {
    textAlign: 'right',
  },
  disclaimerLink: {
    color: '#2ecc71',
    fontFamily: 'VastagoGrotesk',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 10,
      desktop: 10
    }),
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    padding: 20,
  },
  modalContent: {
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    borderRadius: 10,
    padding: 20,
    maxWidth: 800,
    maxHeight: '80%',
    width: '90%',
  },
  modalTitle: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 24,
    color: 'white',
    marginBottom: 15,
    textAlign: 'center',
  },
  modalText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: 'white',
    marginBottom: 10,
    lineHeight: 20,
  },
  modalSection: {
    marginBottom: 20,
  },
  modalSectionTitle: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 18,
    color: 'white',
    marginBottom: 10,
    marginTop: 15,
  },
  closeButton: {
    alignSelf: 'center',
    marginTop: 20,
    backgroundColor: '#2ecc71',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
  },
  closeButtonText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    color: 'white',
    fontSize: 16,
  },
  emailLink: {
    color: '#2ecc71',
    textDecorationLine: 'underline',
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  footerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    opacity: 0.6,
    position: 'relative',
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease',
      cursor: 'pointer',
      ':hover': {
        opacity: 1
      }
    }),
  },
  footerButtonText: {
    color: '#000000',
    fontFamily: 'Playpen Sans',
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 14,
      laptop: 14,
      desktop: 14
    }),
    fontWeight: '500',
    paddingHorizontal: 10,
  },
  tooltip: {
    position: 'absolute',
    bottom: '150%',
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    width: 200,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    opacity: 0,
    visibility: 'hidden',
    transition: 'all 0.3s ease',
    zIndex: 1000,
    pointerEvents: 'none',
  },
  tooltipVisible: {
    opacity: 1,
    visibility: 'visible',
  },
  tooltipText: {
    color: '#000000',
    fontFamily: 'Playpen Sans',
    fontSize: 12,
    lineHeight: 1.4,
    textAlign: 'center',
  },
});

// For web platform, we need to create a hover-enabled component
const HoverableFooter = Platform.OS === 'web'
  ? ({ children }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    return (
      <View
        style={[
          styles.footer,
          isHovered && { opacity: 1 }
        ]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
      </View>
    );
  }
  : View;

export function Footer({ isAdminPage = false, backgroundColor }) {
  const currentYear = new Date().getFullYear();
  const FooterComponent = HoverableFooter;
  const [disclaimerVisible, setDisclaimerVisible] = useState(false);
  const [isDonateHovered, setIsDonateHovered] = useState(false);
  const navigation = useNavigation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        setIsMobile(width <= BREAKPOINTS.mobile);
      }
    };
    
    checkScreenSize();
    
    // if (typeof window !== 'undefined') {
    //   window.addEventListener('resize', checkScreenSize);
    //   return () => window.removeEventListener('resize', checkScreenSize);
    // }
  }, []);


  if (isAdminPage) {
    return null;
  }


  return (
    <View style={[
      styles.footerContainer,
      isMobile && styles.footerContainerMobile,
      backgroundColor ? { backgroundColor, borderTop: 'none' } : null
    ]}>
      {isMobile ? (
        // Mobile layout: stacked vertically
        <>
          <View style={{ alignItems: 'center' }}>
            <View style={[styles.middleSection, { marginBottom: 5 }]}>
              <Text style={styles.middleSectionText}>VENUE REPORT CARD LLC 2025</Text>
            </View>
            <FooterComponent>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <TouchableOpacity
                  onPress={() => Linking.openURL('https://www.dsignable.com')}
                  style={styles.linkContainer}
                >
                  <Text style={styles.link}>DSIGNABLE</Text>
                </TouchableOpacity>
                <Text style={styles.footerText}> {currentYear}</Text>
              </View>
            </FooterComponent>
          </View>
          
          <View style={styles.rightButtonsContainer}>
            <TouchableOpacity
              style={styles.footerButton}
              onPress={() => navigation.navigate('AboutUs')}
            >
              <Text style={styles.footerButtonText}>ABOUT</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.footerButton}
              onPress={() => Linking.openURL('https://buymeacoffee.com/venuereportcard')}
            >
              <Text style={styles.footerButtonText}>DONATE</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.footerButton}
              onPress={() => navigation.navigate('Contact')}
            >
              <Text style={styles.footerButtonText}>CONTACT US</Text>
            </TouchableOpacity>
          </View>
        </>
      ) : (
        // Desktop layout: three sections horizontally
        <>
          <FooterComponent>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={styles.footerText}>CREATED BY TEAM </Text>
              <TouchableOpacity
                onPress={() => Linking.openURL('https://www.dsignable.com')}
                style={styles.linkContainer}
              >
                <Text style={styles.link}>DSIGNABLE</Text>
              </TouchableOpacity>
              <Text style={styles.footerText}> {currentYear}</Text>
            </View>
          </FooterComponent>

          <View style={styles.middleSection}>
            <Text style={styles.middleSectionText}>VENUE REPORT CARD LLC 2025</Text>
          </View>

          <View style={styles.rightButtonsContainer}>
            <TouchableOpacity
              style={styles.footerButton}
              onPress={() => navigation.navigate('AboutUs')}
            >
              <Text style={styles.footerButtonText}>ABOUT</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.footerButton}
              onPress={() => Linking.openURL('https://buymeacoffee.com/venuereportcard')}
              onMouseEnter={() => setIsDonateHovered(true)}
              onMouseLeave={() => setIsDonateHovered(false)}
            >
              <Text style={styles.footerButtonText}>DONATE</Text>
              {Platform.OS === 'web' && (
                <View style={[styles.tooltip, isDonateHovered && styles.tooltipVisible]}>
                  <Text style={styles.tooltipText}>
                    Help us make Venue Report Card even better! Your support enables us to add new features, improve data accuracy, and create a more comprehensive resource for the live events community. Every contribution makes a difference! 🎵
                  </Text>
                </View>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.footerButton}
              onPress={() => navigation.navigate('Contact')}
            >
              <Text style={styles.footerButtonText}>CONTACT US</Text>
            </TouchableOpacity>
          </View>
        </>
      )}

      {/* <View style={[styles.footer, styles.disclaimerContainer]}>
        <TouchableOpacity onPress={() => setDisclaimerVisible(true)}>
          <Text style={styles.disclaimerLink}>DISCLAIMER & TERMS</Text>
        </TouchableOpacity>
      </View> */}

      <Modal
        visible={disclaimerVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setDisclaimerVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <ScrollView showsVerticalScrollIndicator={true}>
              <Text style={styles.modalTitle}>DISCLAIMER & TERMS</Text>
              
              <View style={styles.modalSection}>
                <Text style={styles.modalText}>We are not associated with any venue. The info compiled here was taken directly from venue tech sheets and websites.</Text>
                <Text style={styles.modalText}>All info collected here is subject to change without notice. You should always advance with the venue directly to receive the most up-to-date info.</Text>
                <Text style={styles.modalText}>We know that's not easy, so we created this database. We tried to make it as simple as possible.</Text>
                <Text style={styles.modalText}>Key components here are: consoles, speakers, backline, venue parking.</Text>
              </View>

              <View style={styles.modalSection}>
                <Text style={styles.modalText}><Text style={{fontWeight: '700'}}>PLEASE NOTE:</Text> Backline only covers a drum set as minimum. It has nothing to do with mics or stands.</Text>
                <Text style={styles.modalText}>If you've toured through and know some stuff, please fill it in the spaces open below. When enough people start sending us the same basic info, we'll update. Be sure to hit that submit button!</Text>
              </View>

              <View style={styles.modalSection}>
                <Text style={styles.modalText}>If you think we have incorrect info, let us know at <Text style={styles.emailLink} onPress={() => Linking.openURL('mailto:contact@venuereportcard.com?subject=fix it')}>contact@venuereportcard.com</Text> with subject line: fix it</Text>
                <Text style={styles.modalText}>If you have a useful link with more venue data, please email us at <Text style={styles.emailLink} onPress={() => Linking.openURL('mailto:contact@venuereportcard.com?subject=spec link')}>contact@venuereportcard.com</Text> with subject line: spec link</Text>
                <Text style={styles.modalText}>If you have updated tech info to share about a venue you either own or work at, please email us at <Text style={styles.emailLink} onPress={() => Linking.openURL('mailto:contact@venuereportcard.com?subject=VENUE')}>contact@venuereportcard.com</Text> with subject line: VENUE</Text>
                <Text style={styles.modalText}>We will never share booking or management emails.</Text>
                <Text style={styles.modalText}>If there is a venue you know and love that's missing, please click the + VENUE link at the top right of the form below. It will bring you to a new blank form to fill out and includes a dedicated space for a venue tech spec link to share.</Text>
                <Text style={styles.modalText}>Thank you for your participation. We hope this helps.</Text>
              </View>

              <TouchableOpacity 
                style={styles.closeButton}
                onPress={() => setDisclaimerVisible(false)}
              >
                <Text style={styles.closeButtonText}>Close</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
} 